import { useContext } from 'react';
import { Context } from '../CreateNotice';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';
import dayjs from 'dayjs';
import NoticeTypeIcon from './NoticeTypeIcons';
import { NOTICE_CATEGORIES, NOTICE_TYPES, REASONS } from '../../common/constants';

import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import receiptButton from '../../images/preview_receipt_button.png';

const PreviewImages = (props) => {
  const notice = props.notice;
  const { typography, palette } = useTheme();

  const noticeTypeColor = (noticeTypeCode) => {
    switch (noticeTypeCode) {
      case NOTICE_TYPES.THIRTY_FIVE_LIMIT.value:
        return palette.yellowPale;
      case NOTICE_TYPES.TWENTY_FIVE_LIMIT.value:
        return palette.yellowPale;
      case NOTICE_TYPES.SUSPENSION.value:
        return palette.redPale;
      case NOTICE_TYPES.LIMIT_CANCEL.value:
        return palette.blackPale;
      case NOTICE_TYPES.SUSPENSION_CANCEL.value:
        return palette.blackPale;
      case NOTICE_TYPES.SLOWDOWN.value:
        return palette.brownPale;
    };
  };

  const bgColor = noticeTypeColor(notice.noticeTypeCode);
  const styles = {
    regulationNoticeBackground: {
      width: '516px',
      height: '78px',
      marginBottom: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.blackPale,
    },
    noticePaper: {
      width: '468px',
      height: '58px',
      display: 'flex',
      alignItems: 'center',
    },
    noticeMessage: {
      width: '350px',
      alignItems: 'center',
      justifyContent: 'center',
    },
    noticeMessageText: {
      ...typography.body14,
      margin: 'auto',
    },
    regulationInformationBackground: {
      width: '516px',
      height: '322px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.blackPale,
    },
    regulationInformationPaper: {
      width: '468px',
      height: '302px',
    },
    regulationInformationHeader: {
      marginLeft: '33px',
    },
    regulationInformationHeaderText: {
      ...typography.caption13,
    },
    regulationInformationBody: {
      marginLeft: '64px',
    },
    regulationInformationBodyText: {
      ...typography.body14,
    },
    attentionNoticeBackground: {
      width: '516px',
      height: '78px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: bgColor,
    },
    attentionNoticeText: {
      ...typography.body14,
      width: '330px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  const {
    receivers, loginUser,
  } = useContext(Context);

  const createReceiverNames = (receivers) => {
    const names = receivers.map((receiver) => receiver.name);
    if (names.length === 0) {
      return '';
    }
    if (names.length === 1) {
      return names[0];
    }
    return names[0] + ', ...';
  };

  // 指令番号（4桁の乱数）
  const commandNumber = ('000' + Math.floor(Math.random() * 10000)).slice(-4);

  // 受信者の名前
  const receiverNames = createReceiverNames(Object.values(receivers));

  if (notice.noticeCategoryCode === NOTICE_CATEGORIES.REGULATION.value) {
    // 運転規制/解除
    return (
      <>
        <Container disableGutters sx={styles.regulationNoticeBackground}>
          <Paper sx={styles.noticePaper}>
            <NoticeTypeIcon noticeTypeCode={notice.noticeTypeCode} sx={{ width: '50px', height: '50px', marginLeft: '8px' }} />
            <Stack sx={styles.noticeMessage}>
              <Typography sx={styles.noticeMessageText}>
                {`${loginUser.branchName}-${notice.sectionName}-${commandNumber}`}
              </Typography>
              <Typography sx={styles.noticeMessageText}>
                {`${notice.startStationName} 〜 ${notice.endStationName} 間 `}
                {`${Object.values(NOTICE_TYPES).find((type) => type.value === notice.noticeTypeCode)?.detailLabel}`}
              </Typography>
              <Typography sx={styles.noticeMessageText}>
                {Object.values(REASONS).find((reason) => reason.value === notice.reasonCode)?.label}
              </Typography>
            </Stack>
          </Paper>
        </Container>
        <Container disableGutters sx={styles.regulationInformationBackground}>
          <Paper sx={styles.regulationInformationPaper}>
            <Stack sx={styles.regulationInformationHeader}>
              <Typography sx={styles.regulationInformationHeaderText}>指令通告</Typography>
              <Typography sx={styles.regulationInformationHeaderText}>
                {`指令番号：${loginUser.branchName}-${notice.sectionName}-${commandNumber}`}
              </Typography>
              <Typography sx={styles.regulationInformationHeaderText}>
                {`発信日時：${dayjs().format('M月D日 H時m分')}`}
              </Typography>
            </Stack>
            <Stack direction='row' sx={styles.regulationInformationHeader}>
              <Typography sx={styles.regulationInformationHeaderText}>受信者</Typography>
              {/* TODO: iPadから送信される列番に変更する */}
              <Typography sx={{ ...typography.caption13, margin: '0 13px' }}>{`：${receiverNames}`}</Typography>
              <Typography sx={{ ...typography.caption13, margin: '0 13px' }}>指令者</Typography>
              <Typography sx={{ ...typography.caption13, width: '143px' }} noWrap>{`：${loginUser.name}`}</Typography>
            </Stack>
            <Divider sx={{ marginBottom: '12px' }} />
            <Stack direction='row' sx={{ alignItems: 'center' }}>
              <NoticeTypeIcon
                noticeTypeCode={notice.noticeTypeCode}
                sx={{ width: '50px', height: '50px', marginLeft: '8px' }} />
              <Typography sx={{ ...typography.body16bold, margin: '0 5px' }}>
                {Object.values(NOTICE_TYPES).find((type) => type.value === notice.noticeTypeCode)?.label}
              </Typography>
            </Stack>
            <Stack sx={styles.regulationInformationBody}>
              <Typography sx={styles.regulationInformationBodyText}>
                {`線区：${notice.sectionName}`}
              </Typography>
              <Typography sx={styles.regulationInformationBodyText}>
                {`区間：${notice.startStationName} 〜 ${notice.endStationName} 間`}
              </Typography>
              <Typography sx={styles.regulationInformationBodyText}>
                {`規制：${Object.values(NOTICE_TYPES).find((type) => type.value === notice.noticeTypeCode)?.detailLabel}`}
              </Typography>
              <Typography sx={styles.regulationInformationBodyText}>
                {`事由：${Object.values(REASONS).find((reasons) => reasons.value === notice.reasonCode)?.label}`}
              </Typography>
            </Stack>
            <Box textAlign='center' marginTop='10px'>
              <img src={receiptButton} alt='受領（プレビュー）' />
            </Box>
          </Paper>
        </Container>
      </>
    );
  } else {
    // 注意喚起情報
    return (
      <>
        <Container sx={styles.attentionNoticeBackground}>
          <Paper sx={styles.noticePaper}>
            <NoticeTypeIcon noticeTypeCode={notice.noticeTypeCode} sx={{ width: '50px', height: '50px', marginLeft: '8px' }} />
            <Typography sx={styles.attentionNoticeText}>
              {`${Object.values(NOTICE_TYPES).find((type) => type.value === notice.noticeTypeCode)?.label} `}
              {`${notice.startStationName} 〜 ${notice.endStationName} 間`}
            </Typography>
          </Paper>
        </Container>
      </>
    );
  }
};

export default PreviewImages;

PreviewImages.propTypes = {
  notice: PropTypes.object,
  loginUser: PropTypes.object,
};
